/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
    object, string, bool, shape, number, func,
} from 'prop-types';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import makeStyles from '@material-ui/core/styles/makeStyles';
import LazyLoad from 'react-lazyload';
import generateValidCss from '../../../../helpers/contentstack/generateValidCss';
import Copy from '../../Copy';
import { ifExternalUrl } from '../helper';
import { trackEvent } from '../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import ResponsiveImage from '../../../GraphqlComponents/GraphqlCommonComponents/ResponsiveImage/ResponsiveImage';
import parseStyles from '../../../../helpers/contentstack/parseStyles';
import { getFeatureFlags,  getPresentationFamily } from '../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import { getCurrentPageView } from '../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Selectors';

const useStyles = makeStyles((theme) => ({
    nUp: (data) => ({
        maxWidth: '1400px',
        position: 'relative',
        padding: data?.reference?.[0].presentation_style === 'Showcase Container' ? '10px 10px calc(280px + 4vw)' : '0 10px 0',
        '@media screen and (max-width: 624px)': {
            paddingBottom: data?.reference?.[0].presentation_style === 'Showcase Container' ? 'calc(420px + 4vw)' : '0',
        },
    }),
    nUpLess: {
        maxWidth: '1400px',
        overflow: 'hidden',
        position: 'relative',

    },
    panelGroupBottomMarginDelete: {
        '& a:last-child': {
            marginBottom: '0',
        },
        '& a:nth-last-child(2)': {
            marginBottom: '0',
        },
    },
    categoryPer3row: {
        maxHeight: '700px',
    },
    categoryPer4row: {
        maxHeight: '900px',
    },
    header: {
        marginTop: '14px',
        paddingTop: '1px',
        paddingBottom: '4px',
        marginBottom: '0',
        fontSize: '1rem',
        fontWeight: '800',
        color: theme.palette.sectionHeaderTextColor || '#202020',
    },
    heading_paragraph_wrapper: {
        width: '100%',
        marginTop: '15px',
        marginBottom: '30px',
    },
    heading_paragraph: {
        margin: '0 auto',
        textAlign: 'center',
        width: '95%',
        lineHeight: '1.5em',
        letterSpacing: '.4px',
        fontSize: '1em',
        '& h1,h2,h3,h4,h5,h6,p': {
            margin: '0px 0 15px 0',
            fontSize: 'inherit',
            fontWeight: 'inherit',
        },
    },
    panel: {
        width: '50%',
        height: 'auto',
        marginBottom: '10px',
        textDecoration: 'none',
        fontSize: '0.9rem',
        backgroundSize: 'cover',
        textAlign: 'center',
        color: theme.palette.black,
        '@media screen and (min-width: 625px)': {
            width: '33.33%',
        },
        '&:last-of-type': {
            marginBottom: '16px',
        },
    },
    panelOneItem: {
        width: '100%',
        height: 'auto',
        maxWidth: '362px',
        textDecoration: 'none',
        fontSize: '0.9rem',
        backgroundSize: 'cover',
        textAlign: 'center',
        color: theme.palette.black,
        '&:nth-of-type(1)': {
            marginTop: '0',
        },
        padding: '20px 0 5px',
        border: theme.palette.gfMobileInputBorderBottom || '1px solid #cccccc',
    },
    panelOneItemBorderless: {
        width: '100%',
        height: 'auto',
        maxWidth: '362px',
        textDecoration: 'none',
        fontSize: '0.9rem',
        backgroundSize: 'cover',
        textAlign: 'center',
        color: theme.palette.black,
        marginTop: '20px',
        '&:nth-of-type(1)': {
            marginTop: '0',
        },
    },
    panelTwoItem: {
        marginBottom: '0',
        '&+a': {
            marginBottom: '0 !important',
        },
    },
    panelThreeItem: {
        width: '33.33%',
        height: 'auto',
        textDecoration: 'none',
        fontSize: '0.9rem',
        backgroundSize: 'cover',
        textAlign: 'center',
        color: theme.palette.black,
        marginTop: '20px',
        padding: '5px',
    },
    panelGroup: {
        padding: '0',
        display: 'flex',
        flexWrap: 'wrap',
        flexFlow: 'row wrap',
        alignItems: 'flex-start',
        alignContent: 'flex-end',
        flexDirection: 'row',
        justifyContent: 'center',
    },
    divider: (data) => ({
        backgroundColor: data.reference[0].vertical_entry_dividers?.divider_color || '#ccc',
        width: '100%',
        height: data.reference[0].vertical_entry_dividers?.width || '1px',
    }),
    root: {
        padding: theme.spacing(3, 0, 0, 0),
    },
    containerLink: {
        textDecoration: 'none',
    },
    imageContainer: {
        height: '90%',
        '& img': {
            width: '77px',
            height: '80px',
            clipPath: 'ellipse(36px 36px at 38px 41px)',
            maxWidth: 'inherit',
            '&:after': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                left: 0,
                height: '100%',
                width: '100%',
                background: 'linear-gradient(115.42deg, rgba(0, 0, 0, 0.6) 13.4%, rgba(0, 0, 0, 0.375) 42.44%, rgba(0, 0, 0, 0) 68.26%)',
            },
        },
    },
    gradientImageContainer: {
        height: '100%',
        width: '95%',
        position: 'relative',
        '& img': {
            height: 200,
            maxWidth: '95%',
            objectFit: 'contain',
        },
        '& picture:after': {
            content: '""',
            display: 'block',
            position: 'absolute',
            top: 0,
            left: 3,
            height: '97%',
            width: '100%',
            background: 'linear-gradient(0deg, rgba(0, 0, 0, 0.6) 13.4%, rgba(0, 0, 0, 0.375) 42.44%, rgba(0, 0, 0, 0) 68.26%)',
        },
    },
    gradientMessageContainer: {
        padding: '0 3%',
        position: 'absolute',
        bottom: 25,
        boxSizing: 'border-box',
        textAlign: 'center',
        width: '48%',
        color: theme.palette.white,
        fontFamily: 'LatoBold',
        fontWeight: 900,
        fontSize: '1.2em',
    },
    imageContainerOneItem: {
        height: '100%',
        '& img': {
            width: '100%',
            paddingBottom: '5px',
            objectFit: 'contain',
        },
    },
    messageContainer: {
        boxSizing: 'border-box',
        padding: '0 2%',
        position: 'relative',
        marginTop: '-10px',
    },
    messageContainerOneItem: {
        boxSizing: 'border-box',
        position: 'relative',
        marginTop: '-10px',
    },
    Standard: theme.palette.nup?.Standard,
    StandardMobile: {
        width: '99%',
        height: 'auto',
    },
    Medium: theme.palette.nup?.Medium,
    Small: theme.palette.nup?.Small,
    viewMoreWrapper: {
        width: 'calc(100vw)',
        bottom: '0',
        height: '40px',
        display: 'block',
        backgroundColor: 'rgba(255,255,255,0)',
    },
    viewMoreGradientWrapper: {
        left: '0',
        width: 'calc(100vw)',
        bottom: '0',
        height: '200px',
        display: 'block',
        position: 'absolute',
        backgroundColor: 'rgba(255,255,255,0)',
        backgroundImage: 'linear-gradient(to bottom, rgba(255, 255, 255, 0) 0, rgba(255, 255, 255, .9) 60%)',
    },
    viewMore: {
        left: 'calc(50% - 0px)',
        color: theme.palette.common?.white,
        width: 'calc(35vw)',
        transform: 'translate(-50%, 0)',
        fontSize: '16px',
        boxShadow: '0 16px 16px -14px rgba(0, 0, 0, .7)',
        fontWeight: '700',
        borderRadius: '5px',
        backgroundColor: theme.palette.text?.primary,
        lineHeight: '1.75',
        fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
        textTransform: 'uppercase',
        border: '0',
        cursor: 'pointer',
        outline: 'none',
        position: 'absolute',
        bottom: '24px',
        userSelect: 'none',
    },
    viewLess: {
        bottom: '5px',
    },
    showcaseHeadingFirstWord: {
        color: '#5c656b',
    },
    showcaseHeadingRemaining: {
        color: '#4793b1',
    },
    itemContainer: {
        width: '100%',
        transform: 'translateZ(0)',
        overflowY: 'auto',
        flexWrap: 'nowrap',
        justifyContent: 'flex-start',
    },
    Imglink: {
        margin: '0 3.5%',
        fontSize: '12px',
    },
    h2Text: {
        '& $h2': {
            fontSize: '18px',
            marginBottom: 'inherit',
            fontFamily: 'PlayfairDisplayRegular, serif',
            fontWeight: 'normal',
            marginTop: 0,
        },
    },
}));

const NUpMobile = ({
    data, isBot, track, featureFlags, pageView, presentationFamily,
}) => {
    const classes = useStyles(data);

    const [showLess, toggleViewMore] = React.useState(true);
    const onToggleViewMore = () => toggleViewMore(!showLess);
    const flowerBrand = presentationFamily === 'flower';
    let imageContainerClass = 'imageContainer';
    let messageContainerClass = 'messageContainer';
    let panelClass = 'panel';
    if (!data?.reference?.[0]) return <></>;
    if (data?.reference?.[0]) {
        const banner = data.reference[0];
        const hasDividers = banner.vertical_entry_dividers?.add_vertical_dividers;
        const showDividers = hasDividers && banner.mobile_row_item_count === 1;
        const subSiloImoc = flowerBrand && pageView.pageType === 'category';
        if (banner.gradient_overlay === true) {
            imageContainerClass = 'gradientImageContainer';
            messageContainerClass = 'gradientMessageContainer';
        }
        if (banner.mobile_row_item_count === 1) {
            panelClass = (!banner.hide_panel_borders_on_mobile) ? 'panelOneItem' : 'panelOneItemBorderless';
            imageContainerClass = 'imageContainerOneItem';
            messageContainerClass = 'messageContainerOneItem';
        } else if (banner.reference.length === 3) {
            panelClass = 'panelThreeItem';
        }
        const getContainerBackgroundStyles = () => {
            if (!banner.background?.add_background) return {};
            const bgVal = banner.background;
            const backgroundStyles = {
                backgroundColor: bgVal?.solid_background_color,
            };
            if (bgVal?.images) {
                backgroundStyles.backgroundImage = `url("${bgVal?.images?.url}")`;
                backgroundStyles.backgroundSize = 'cover';
            }
            return backgroundStyles;
        };
        const loadCssRowBase = ((banner.styles?.per_row || banner?.mobile_row_item_count) === 3 ? classes.categoryPer3row : classes.categoryPer4row);
        const handleClick = (e, title) => {
            if (title === 'Celebrations Passport'
                // VARIANT A is "Passport Enrolment Page"
                // CONTROL is "Passport Side Bar"
                && featureFlags['which-passport-sidebar-enabled-variant']?.variant === 'CONTROL') {
                e.preventDefault();
            }
        };

        const renderHeading = (nupData) => {
            /*
             * @description handles conditional header rendering and avoids nested ternaries in markup.
             * @param {object} nup data
             * @return heading markup for eithe standard or t3 showcase heading
            */

            const showcaseHeader = (header) => {
                /*
                 * @description handles params and markup for larger showcase nup header as designed by t3.
                 * @param {object} header data
                 * @return markup with 2 font colors as described in classes in this component.
                */
                const firstSpace = header.search(' ');
                const firstWord = header.substring(0, firstSpace);
                const remainingString = header.substring(firstSpace);
                return (
                    <h2 className={`${classes.header} ${classes.h2Text} ${banner.heading_styles?.background_stroke ? classes.background_stroke : ''}`} style={parseStyles(banner.heading_styles?.font_styles)}>
                        <span className={classes.showcaseHeadingFirstWord}>{firstWord}</span><br />
                        <span className={classes.showcaseHeadingRemaining}>{remainingString}</span>
                    </h2>
                );
            };

            if (nupData.heading && banner.presentation_style !== 'Showcase Container') {
                return (
                    <div
                        className={`${classes.header} ${classes.h2Text} ${nupData.heading_styles?.background_stroke && classes.background_stroke}`}
                        style={parseStyles(nupData.heading_styles?.font_styles)}
                        data-testid="heading-font-styles"
                    ><ReactMarkdown source={nupData.heading} />
                    </div>
                );
            }
            if (nupData.presentation_style === 'Showcase Container') {
                return showcaseHeader(nupData.heading);
            }
            return null;
        };

        return (
            <div className={`${banner.reference.length > 3 && classes.panelGroupBottomMarginDelete} ${(showLess && banner.reference.length > 6 && !banner.disable_view_more) ? `${classes.nUpLess} ${loadCssRowBase}` : classes.nUp} ${subSiloImoc && classes.NupSciSlider}`} style={getContainerBackgroundStyles()}>
                { renderHeading(banner) }
                {
                    banner.heading_paragraph ? (
                        <div className={classes.heading_paragraph_wrapper}>
                            <div className={classes.heading_paragraph}>
                                <ReactMarkdown source={banner.heading_paragraph} />
                            </div>
                        </div>
                    ) : null
                }
                <div className={`${classes.panelGroup} ${classes.itemContainer}`}>
                    {
                        banner.reference.map((panelItem, index, array) => (
                            <>
                                <Link
                                    key={panelItem?.linking?.link?.href}
                                    to={panelItem?.linking?.link?.href}
                                    data-testid="nup-panel"
                                    aria-hidden="true"
                                    title={panelItem?.linking?.link?.title}
                                    // if only two, remove bottom margin altogether and allow container margin to suffice
                                    className={`${classes[panelClass]} ${banner?.reference?.length === 2 && classes.panelTwoItem} ${classes.Imglink}`}
                                    data-ga-category={panelItem?.linking?.tracking_event_category || ''}
                                    data-ga-action={panelItem?.linking?.tracking_event_action || ''}
                                    data-ga-label={panelItem?.linking?.tracking_event_label || ''}
                                    onClick={(e) => {
                                        handleClick(e, panelItem?.linking?.link?.title);
                                        track({
                                            eventCategory: panelItem?.linking?.tracking_event_category || '',
                                            eventAction: panelItem?.linking?.tracking_event_action || '',
                                            eventLabel: panelItem?.linking?.tracking_event_label || '',
                                            eventName: 'feature_interaction', // Requird for GA4 feature_click
                                        });
                                        ifExternalUrl(e, panelItem?.linking?.link?.href);
                                    }}
                                >
                                    {panelItem?.image_mobile
                                        /* If a backgroud image is provided, it will render as default product tile
                                        else it will render a div of provided dimension / presentation style */
                                        ? (
                                            <>
                                                <div className={classes[imageContainerClass]}>
                                                    {isBot ? <img src={panelItem?.image_mobile?.url} alt={panelItem?.image_mobile?.title} />
                                                        : (
                                                            <LazyLoad>
                                                                {!panelItem.image_mobile
                                                                    && (
                                                                        <ResponsiveImage
                                                                            params={{ desktop: 'quality=90', mobile: 'quality=80', tablet: 'quality=85' }}
                                                                            path={panelItem.image.url}
                                                                            alt={panelItem.image.title}
                                                                            style={!subSiloImoc ? { ...(generateValidCss(panelItem?.container_attributes, true) || {}) } : {}}
                                                                        />
                                                                    )}
                                                                <ResponsiveImage
                                                                    params={{ desktop: 'quality=90', mobile: 'quality=80', tablet: 'quality=85' }}
                                                                    path={panelItem.image_mobile.url}
                                                                    alt={panelItem.image_mobile.title}
                                                                    style={!subSiloImoc ? { ...(generateValidCss(panelItem?.container_attributes, true) || {}) } : {}}
                                                                />
                                                            </LazyLoad>
                                                        )}
                                                </div>
                                                <div className={classes[messageContainerClass]} style={generateValidCss(panelItem?.message_styling, true)} data-testid="nup-panel-msg">
                                                    <Copy data={panelItem?.message_collection} />
                                                </div>
                                            </>
                                            /* Added Copy components instead of manual switch case
                                            to avoid code repetition and to add more functionality to n-up panel.
                                            We can use Panel component directly but background image logic has not been implemented in it yet */
                                        )
                                        : (
                                            <div style={generateValidCss(panelItem?.message_styling, true)} className={classes.generalMessageStylingClass} data-testid="nup-panel-msg">
                                                <div className={classes[banner.presentation_style]} style={generateValidCss(panelItem?.container_attributes, true)}>
                                                    <Copy data={panelItem?.message_collection} />
                                                </div>
                                            </div>
                                        )}
                                </Link>
                                {showDividers && index < array.length - 1 && <div className={classes.divider} data-testid="panel-divider" />}
                            </>
                        ))
                    }
                </div>
                {banner.reference?.length > 6 && !banner.disable_view_more && (
                    <div className={showLess ? classes.viewMoreGradientWrapper : classes.viewMoreWrapper}>
                        <button type="button" className={`${classes.viewMore} ${!showLess ? classes.viewLess : ''}`} onClick={onToggleViewMore}>
                            {showLess ? <span>View More</span> : <span>View Less</span>}
                        </button>
                    </div>
                )}
            </div>
        );
    }
    return <></>;
};

NUpMobile.propTypes = {
    isBot: bool,
    data: shape({
        disable_view_more: bool,
        display_on: string,
        full_width: bool,
        heading: string,
        heading_styles: object,
        mobile_display_count: number,
        mobile_row_item_count: number,
        presentation_style: string,
        row_count: number,
        title: string,
        unique_selector: shape({ selector_id: string }),
    }).isRequired,
    track: func.isRequired,
    featureFlags: shape({
        'which-passport-sidebar-enabled-variant': shape({
            variant: string,
        }),
    }),
    pageView: shape({
        pageType: string.isRequired,
    }).isRequired,
    presentationFamily: string.isRequired,
};

NUpMobile.defaultProps = {
    isBot: false,
    featureFlags: {},
};

const mapStateToProps = (state) => ({
    featureFlags: getFeatureFlags(state),
    pageView: getCurrentPageView(state),
    presentationFamily: getPresentationFamily(state),
});
const mapDispatchToProps = (dispatch) => ({
    track: bindActionCreators(trackEvent, dispatch),
});

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(NUpMobile);
